'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';

/**
 * Shared desktop column for transient prompts (cookie consent,
 * passkey prompt, push prompt, discord install banner, critical
 * approval toast). The column is position-aware (see `posClass`
 * below): top-RIGHT on the bare canvas routes (`/workspace`,
 * `/w/…`), where that corner has zero chrome, and top-LEFT under
 * the fixed header everywhere else in the portal — clear of the
 * right-side chat sidebar.
 *
 * Mobile is untouched: each caller still mounts its `<BottomSheet>`
 * (or hand-rolled docked sheet for `<CriticalToast>`) so the
 * drag-to-dismiss + full-width slide-in survives. The portal
 * targets returned below are `null` on mobile, which makes any
 * `ToastStackPortal` consumer render nothing in that case — the
 * caller then falls back to its mobile path.
 *
 * Stack ordering:
 *   - `critical` slot renders first (top of the column).
 *   - `default` slot renders below.
 * Within a slot, render order follows mount order — first-mounted
 * banners sit nearest the top of the column.
 */
export type ToastStackSlot = 'critical' | 'default';

type SlotTargets = Record<ToastStackSlot, HTMLElement | null>;

const Ctx = createContext<SlotTargets>({ critical: null, default: null });

/**
 * Mount once near the top of the provider tree. Renders an
 * invisible fixed column on desktop; portals into the `critical`
 * div land above portals into the `default` div, both gapped
 * uniformly so consecutive banners don't touch.
 *
 * The wrapper is `pointer-events-none` and each child slot is
 * `pointer-events-auto` so the column itself never intercepts
 * canvas drags between toasts.
 */
export function ToastStackProvider({ children }: { children: ReactNode }) {
  const [criticalEl, setCriticalEl] = useState<HTMLElement | null>(null);
  const [defaultEl, setDefaultEl] = useState<HTMLElement | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  // The canvas routes (`/workspace`, published `/w/…`) render no top
  // header and no right-side chat sidebar — the surface is the bare
  // canvas, so the top-RIGHT corner is the only one with zero chrome
  // (the rail + flyout panels live on the left, the zoom pill is
  // bottom-centre). Everywhere else (the portal) keeps the column
  // top-LEFT under the fixed header, clear of the right chat sidebar.
  const onCanvas =
    !!pathname &&
    (pathname === '/workspace' ||
      pathname.startsWith('/workspace/') ||
      pathname.startsWith('/w/'));
  const posClass = onCanvas ? 'right-3 top-3' : 'left-3 top-14';

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mq.matches);
    setMounted(true);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <Ctx.Provider value={{ critical: criticalEl, default: defaultEl }}>
      {children}
      {mounted && isDesktop && (
        <div
          aria-live="polite"
          // z-[170] keeps action-required toasts (spending approvals, etc.)
          // ABOVE the edge panels (My Library / Notifications open at z-[150])
          // while staying below user menus + dialogs (z-[220]+).
          className={`pointer-events-none fixed ${posClass} z-[170] flex w-[min(384px,calc(100vw-1.5rem))] flex-col gap-2`}
        >
          <div ref={setCriticalEl} className="flex flex-col gap-2" />
          <div ref={setDefaultEl} className="flex flex-col gap-2" />
        </div>
      )}
    </Ctx.Provider>
  );
}

/**
 * Returns the DOM node to portal into for the requested slot, or
 * `null` on mobile / before hydration. Callers that already own
 * their own mobile path should branch on this — desktop renders
 * via the portal, mobile falls back to the legacy bottom-sheet.
 */
export function useToastStackTarget(
  slot: ToastStackSlot = 'default',
): HTMLElement | null {
  return useContext(Ctx)[slot];
}

/**
 * Convenience portal wrapper. Drops children into the requested
 * slot, restoring `pointer-events: auto` for the portaled card
 * (the outer column is `pointer-events-none`). Returns `null` on
 * mobile so the caller's fallback chain takes over.
 */
export function ToastStackPortal({
  slot = 'default',
  children,
}: {
  slot?: ToastStackSlot;
  children: ReactNode;
}) {
  const target = useToastStackTarget(slot);
  if (!target) return null;
  return createPortal(
    <div className="pointer-events-auto">{children}</div>,
    target,
  );
}
